import Image from 'next/image';
import React from 'react';

import { ConditionalWrapper } from '@/components/conditional-wrapper';
import { Heading } from '@/components/heading';
import { Text } from '@/components/text';
import { WebBrowserWrapper } from '@/components/web-browser-wrapper';
import { tv } from '@/utils/styles';

export interface LandingProps {
  actions?: React.ReactNode;
  heading?: string;
  subHeading?: string | React.ReactNode;
  footerText?: string | React.ReactNode;
  imageUrl?: string;
  imageAlt?: string;
  imageWidth?: number;
  imageHeight?: number;
  variant?: 'basic' | 'browser';
  order?: 'default' | 'reverse';
}

const Landing = ({
  actions,
  heading,
  subHeading,
  footerText,
  imageUrl,
  imageAlt = '',
  variant = 'browser',
  imageHeight = 390,
  imageWidth = 900,
  order = 'default',
}: LandingProps) => {
  const { base, actionsWrapper, sub, header, footer, contentWrapper } = styles({ order });

  return (
    <article className={base()}>
      <div className={contentWrapper()}>
        <header className={header()}>
          {heading ? (
            <Heading as="h1" className="mt-0">
              {heading}
            </Heading>
          ) : null}
          {subHeading ? <Text className={sub()}>{subHeading}</Text> : null}
        </header>
        <div className={actionsWrapper()}>{actions}</div>
        {footerText ? <Text className={footer()}>{footerText}</Text> : null}
      </div>
      {imageUrl ? (
        <ConditionalWrapper
          condition={variant === 'browser'}
          // eslint-disable-next-line react/no-unstable-nested-components
          wrapper={(children) => <WebBrowserWrapper>{children}</WebBrowserWrapper>}
        >
          <Image loading="lazy" width={imageWidth} height={imageHeight} src={imageUrl} alt={imageAlt} />
        </ConditionalWrapper>
      ) : null}
    </article>
  );
};

const styles = tv({
  slots: {
    base: 'flex flex-col items-center gap-12 pt-12',
    contentWrapper: 'flex flex-col items-center',
    actionsWrapper: 'flex gap-4',
    header: 'max-w-xl text-center',
    sub: 'text-baseSm italic',
    footer: 'mt-4 text-sm italic text-grey-500',
  },
  variants: {
    order: {
      default: {},
      reverse: {
        base: 'mt-4 flex-col-reverse gap-0',
      },
    },
  },
});

export { Landing };
